import { RiMapPinLine } from '@remixicon/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useGoogleMaps from 'src/hooks/useGoogleMaps';
import { Address, fromGooglePlace } from 'src/utils/address/convertAddress';

import { AgAutocomplete, AgAutocompleteProps } from './AgAutocomplete';
import { AgText } from './AgText';

type PlacePrediction = {
  description: string;
  place_id: string;
  structured_formatting?: { main_text: string; secondary_text: string };
};

export interface AgGooglePlacesAutocompleteProps
  extends Omit<
    AgAutocompleteProps<PlacePrediction>,
    | 'options'
    | 'getOptionLabel'
    | 'getOptionKey'
    | 'renderOption'
    | 'filterOptions'
    | 'onInputChange'
    | 'value'
    | 'onChange'
    | 'isLoading'
    | 'isClearable'
    | 'noOptionsText'
  > {
  /** The display string for the input (controlled externally) */
  value?: string;
  /** Called with the resolved address when a prediction is selected */
  onChange: (address: Address | null) => void;
  /** Called with raw input text on every keystroke */
  onInputChange?: (value: string) => void;
  /** Error message displayed below the input */
  errorMessage?: string;
}

export function AgGooglePlacesAutocomplete({
  value = '',
  onChange,
  onInputChange,
  errorMessage,
  placeholder = 'Search address...',
  ...props
}: AgGooglePlacesAutocompleteProps) {
  const { placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading } = useGoogleMaps();

  const [selectedPrediction, setSelectedPrediction] = useState<PlacePrediction | null>(() =>
    value ? { description: value, place_id: '' } : null
  );

  // Track whether value changes are from user interaction (typing/selecting)
  // vs external (form reset, edit mode population)
  const isUserDriven = useRef(false);
  // Skip onInputChange/prediction search when it fires as a side effect of selection
  const isSelecting = useRef(false);

  useEffect(() => {
    if (isUserDriven.current) {
      isUserDriven.current = false;
      return;
    }
    if (value) {
      setSelectedPrediction({ description: value, place_id: '' });
    } else {
      setSelectedPrediction(null);
    }
  }, [value]);

  const handleInputChange = useCallback(
    (inputText: string) => {
      if (isSelecting.current) {
        isSelecting.current = false;
        return;
      }
      isUserDriven.current = true;
      // Typed text is the committed value: on blur AgAutocomplete snaps the input back to it.
      setSelectedPrediction(inputText ? { description: inputText, place_id: '' } : null);
      onInputChange?.(inputText);
      if (inputText) {
        getPlacePredictions({ input: inputText, types: ['address'] });
      }
    },
    [onInputChange, getPlacePredictions]
  );

  const handleSelection = useCallback(
    (prediction: PlacePrediction | null) => {
      if (!prediction) {
        setSelectedPrediction(null);
        onChange(null);
        return;
      }

      isSelecting.current = true;
      setSelectedPrediction(prediction);

      if (prediction.place_id && placesService) {
        placesService.getDetails(
          { placeId: prediction.place_id, fields: ['address_components', 'formatted_address'] },
          (placeDetails: google.maps.places.PlaceResult | null) => {
            if (placeDetails?.address_components) {
              onChange(fromGooglePlace(placeDetails.address_components as any));
            }
          }
        );
      }
    },
    [placesService, onChange]
  );

  const predictions = useMemo(() => (placePredictions ?? []) as PlacePrediction[], [placePredictions]);

  const getOptionLabel = useCallback((p: PlacePrediction) => p.description || '', []);
  const getOptionKey = useCallback((p: PlacePrediction) => p.place_id || p.description, []);

  return (
    <div className="flex w-full flex-col gap-2">
      <AgAutocomplete<PlacePrediction>
        {...props}
        placeholder={placeholder}
        value={selectedPrediction}
        options={predictions}
        getOptionLabel={getOptionLabel}
        getOptionKey={getOptionKey}
        filterOptions={false}
        allowsEmptyCollection
        onInputChange={handleInputChange}
        onChange={handleSelection}
        isLoading={isPlacePredictionsLoading}
        isClearable
        noOptionsText="Type to search for an address"
        startContent={<RiMapPinLine className="h-4 w-4" />}
      />
      {errorMessage && (
        <AgText size="13" className="text-red-text">
          {errorMessage}
        </AgText>
      )}
    </div>
  );
}

AgGooglePlacesAutocomplete.displayName = 'AgGooglePlacesAutocomplete';
